import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import categoryService from "app/common/services/categoryService";
import { TABLE_ENDPOINT, TABLE_ENDPOINT_WEBSITES_ONLY } from "app/pages/industry-analysis/players/const";
import type { IRawData } from "app/pages/industry-analysis/players/table/types";
import { extractPrimaryCategoryFromParams } from "app/pages/industry-analysis/routes/routeUtils";
import DurationService from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";

import { isUseDomainsOnly } from "../utils/getUseDomainsOnly";

interface IMarketPlayersQueryParams {
    category: string;
    country: string;
    duration: string;
    comparedDuration: string;
    webSource: string;
    useDomainsOnly: string;
    includeSubdomains: boolean;
}

interface IMarketPlayersData {
    rawData: IRawData;
    risingAndDecliningRawData: IRawData;
}

const buildApiQueryParams = (params: IMarketPlayersQueryParams) => {
    const { category, country, duration, comparedDuration, webSource, includeSubdomains } = params;
    const primaryCategory = extractPrimaryCategoryFromParams(category);
    const categoryObject = categoryService.categoryQueryParamToCategoryObject(primaryCategory);
    const { from, to, isWindow, compareFrom, compareTo } = DurationService.getDurationData(
        duration,
        comparedDuration,
    ).forAPI;

    return {
        from,
        to,
        country,
        isWindow,
        keys: categoryObject.forApi,
        categoryHash: categoryObject.categoryHash,
        webSource,
        compareFrom,
        compareTo,
        includeSubdomains,
    };
};

const fetchMarketPlayersData = async (params: IMarketPlayersQueryParams): Promise<IMarketPlayersData> => {
    const useDomainsOnly = isUseDomainsOnly(params.useDomainsOnly);
    const fetchService = DefaultFetchService.getInstance();
    const queryParams = buildApiQueryParams(params);

    if (!useDomainsOnly) {
        const rawData = await fetchService.get<IRawData>(TABLE_ENDPOINT, queryParams);
        return { rawData, risingAndDecliningRawData: rawData };
    }

    const [risingAndDecliningRawData, rawData] = await Promise.all([
        fetchService.get<IRawData>(TABLE_ENDPOINT, queryParams),
        fetchService.get<IRawData>(TABLE_ENDPOINT_WEBSITES_ONLY, queryParams),
    ]);

    return { rawData, risingAndDecliningRawData };
};

interface IMarketPlayersQueryParams {
    category: string | undefined;
    country: string | undefined;
    duration: string | undefined;
    comparedDuration: string | undefined;
    webSource: string | undefined;
    useDomainsOnly: string | undefined;
    includeSubdomains: boolean | undefined;
}

export const useMarketPlayersData = () => {
    const { params } = useCurrentStateAndParams<IMarketPlayersQueryParams>();
    const {
        category = "",
        country = "",
        duration = "",
        comparedDuration = "",
        webSource = "",
        useDomainsOnly = "",
        includeSubdomains = true,
    } = params;

    const queryParams: IMarketPlayersQueryParams = {
        category,
        country,
        duration,
        comparedDuration,
        webSource,
        useDomainsOnly,
        includeSubdomains,
    };

    return useQuery<IMarketPlayersData>({
        queryKey: [TABLE_ENDPOINT, queryParams],
        queryFn: () => fetchMarketPlayersData(queryParams),
    });
};
